/**
 * NEXORAX DIGITAL OS - NEXORAX EDGE SOCIAL PUBLISHER (AYRSHARE CORE ENGINE)
 * Version: 2026.05.25
 * Framework: React 19 + Tailwind CSS + Lucide Icons + Recharts
 * Distributed Host Sync: Hetzner Node (5.161.42.190)
 * 
 * COPYRIGHT DECLARATION & PATENT PROTECTION:
 * Copyright (c) 2026 NexoraX Digital Ltd (SC887095). All Rights Reserved.
 * Protected under UK and international patent-readiness protections.
 */

import React, { useState, useEffect } from "react";
import { MasterAutomationEngine } from "../lib/NexoraxEdgeHub";
import { motion, AnimatePresence } from "motion/react";
import { NexoraXViralSwarm } from "./NexoraXViralSwarm";
import {
  Globe,
  Sparkles,
  Terminal,
  Database,
  CheckCircle,
  Zap,
  Calendar,
  Layers,
  TrendingUp,
  Users,
  Key,
  FileText,
  Activity,
  UserCheck,
  Send,
  Plus,
  Trash2,
  Clock,
  Cpu,
  RefreshCw,
  Code2,
  Lock,
  MessageSquare,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  Share2,
  PlusCircle,
  AlertTriangle,
  Play
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";

// Shared Types
interface ScheduledPost {
  id: string;
  content: string;
  platforms: string[];
  scheduledTime: string;
  status: "SCHEDULED" | "SENT" | "FAILED";
  mediaUrl?: string;
  timestamp: string;
}

interface DeveloperLog {
  timestamp: string;
  method: string;
  endpoint: string;
  status: number;
  response: string;
}

export const NexoraXSocialEngine: React.FC = () => {
  // Current Active Sub-Section of the Social Manager
  const [activePanel, setActivePanel] = useState<
    "composer" | "calendar" | "analytics" | "integrations" | "api" | "profile" | "viralswarm"
  >("viralswarm");

  // LinkedIn Overhaul Biometrics State
  const [executiveName, setExecutiveName] = useState(() => localStorage.getItem("NEX_EXEC_NAME") || "Lead Director");
  const [executiveHeadline, setExecutiveHeadline] = useState(() => localStorage.getItem("NEX_EXEC_HEADLINE") || "Director & Founder | Senior Web Architect | NexoraX Digital Enterprise | AI Systems & Voice Matrix");
  const [executiveAbout, setExecutiveAbout] = useState(() => localStorage.getItem("NEX_EXEC_ABOUT") || "Architecting the next generation of global digital infrastructure. As the Director of NexoraX Digital Enterprise, I bridge the gap between complex web architecture, NexoraX AI voice automation, and hyper-conversion UI/UX design. Our mission is to engineer autonomous, high-velocity digital ecosystems that provide unparalleled market dominance for government and enterprise-level clients.");
  const [executiveExperience, setExecutiveExperience] = useState(() => {
    const cached = localStorage.getItem("NEX_EXEC_EXP");
    if (cached) return JSON.parse(cached);
    return [
      {
        id: "exp-1",
        title: "Founder & Director",
        company: "NexoraX Digital Enterprise",
        duration: "2024 - Present",
        description: "Leading the development of the NexoraX Digital OS, focusing on AI-driven voice telephony routing, global reconnaissance intelligence nodes, and NexoraX charitable foundations."
      },
      {
        id: "exp-2",
        title: "Senior Web Architect & Design Systems Lead",
        company: "NexoraX Digital",
        duration: "2022 - 2024",
        description: "Architecting mission-critical, glassmorphic design systems and automated intelligence engines that optimize customer acquisition through high-conversion UI/UX and voice matrix integration."
      }
    ];
  });

  const [savingProfile, setSavingProfile] = useState(false);

  // Post Composer State
  const [composeText, setComposeText] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([
    "linkedin",
    "twitter"
  ]);
  const [scheduleTime, setScheduleTime] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [isInstantPost, setIsInstantPost] = useState(true);

  // Master Lists (Saved in local storage for persistence & realism)
  const [scheduledList, setScheduledList] = useState<ScheduledPost[]>([]);
  const [devLogs, setDevLogs] = useState<DeveloperLog[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  // API Playground States
  const [apiEndpoint, setApiEndpoint] = useState<string>("/api/post");
  const [apiMethod, setApiMethod] = useState<string>("POST");
  const [apiPayload, setApiPayload] = useState<string>(
    JSON.stringify(
      {
        post: "NexoraX Global Infrastructure achieves a consistent 0.8s Handshake standard. Commencing full scalability optimization.",
        platforms: ["linkedin", "twitter"],
        mediaUrls: ["https://images.unsplash.com/photo-1550751827-4bd374c3f58b"]
      },
      null,
      2
    )
  );
  const [apiResponse, setApiResponse] = useState<string>("");
  const [isApiLoading, setIsApiLoading] = useState(false);

  // Developer Keys
  const [apiKey, setApiKey] = useState("NEX_AYR_LIVE_SEC_887095_X_KEY_PF89");
  const [profileKey, setProfileKey] = useState("NEX_PROFILE_ID_DIRECTOR_M");
  const [isKeysVisible, setIsKeysVisible] = useState(false);

  // Integration Toggle Switches
  const [integrationsFullState, setIntegrationsFullState] = useState({
    linkedin: { enabled: true, account: "NexoraX Digital Enterprise", followers: "18,450" },
    twitter: { enabled: true, account: "@NexoraxCorp", followers: "34,120" },
    facebook: { enabled: true, account: "NexoraX Digital Ltd", followers: "12,890" },
    instagram: { enabled: false, account: "Not Linked", followers: "0" },
    youtube: { enabled: false, account: "Not Linked", followers: "0" },
    reddit: { enabled: true, account: "u/Nexorax", followers: "4,500" }
  });

  // Mock initial scheduler load
  useEffect(() => {
    const cachedScheduled = localStorage.getItem("NEXORAX_SOCIAL_SCHEDULED");
    if (cachedScheduled) {
      setScheduledList(JSON.parse(cachedScheduled));
    } else {
      const presets: ScheduledPost[] = [
        {
          id: "SCHED-1",
          content: "The Gamel Strike Matrix version 2.0 has successfully integrated deep backup mirror routes to the NexoraX Resource Library on Google Drive. Compliance core validated.",
          platforms: ["linkedin", "tumblr"],
          scheduledTime: "2026-05-30T10:00:00Z",
          status: "SCHEDULED",
          mediaUrl: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b",
          timestamp: new Date().toISOString()
        },
        {
          id: "SCHED-2",
          content: "Director Lead Director announces complete allocation streams routing 1% Community Allocation toDunfermline & National Charities. NexoraX, direct-billing logistics initialized.",
          platforms: ["linkedin", "twitter", "facebook"],
          scheduledTime: "2026-06-02T14:30:00Z",
          status: "SCHEDULED",
          timestamp: new Date().toISOString()
        }
      ];
      setScheduledList(presets);
      localStorage.setItem("NEXORAX_SOCIAL_SCHEDULED", JSON.stringify(presets));
    }

    const cachedLogs = localStorage.getItem("NEXORAX_SOCIAL_DEV_LOGS");
    if (cachedLogs) {
      setDevLogs(JSON.parse(cachedLogs));
    } else {
      const systemPresets: DeveloperLog[] = [
        {
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          method: "POST",
          endpoint: "/api/post",
          status: 200,
          response: JSON.stringify({ status: "success", post_id: "ayr_post_9918231", updated_platforms: ["twitter", "linkedin"] }, null, 2)
        },
        {
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          method: "GET",
          endpoint: "/api/analytics/summary",
          status: 200,
          response: JSON.stringify({ total_impressions: 142010, engagement_rate: "4.89%" }, null, 2)
        }
      ];
      setDevLogs(systemPresets);
      localStorage.setItem("NEXORAX_SOCIAL_DEV_LOGS", JSON.stringify(systemPresets));
    }
  }, []);

  // Show customized system toast alerts
  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Helper trigger for save and reload state
  const saveScheduledList = (newList: ScheduledPost[]) => {
    setScheduledList(newList);
    localStorage.setItem("NEXORAX_SOCIAL_SCHEDULED", JSON.stringify(newList));
  };

  const saveDevLogs = (newLogs: DeveloperLog[]) => {
    setDevLogs(newLogs);
    localStorage.setItem("NEXORAX_SOCIAL_DEV_LOGS", JSON.stringify(newLogs));
  };

  // Platform Selector handler
  const togglePlatform = (platform: string) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter((p) => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  // Content enhancer using simulated high-fidelity AI triggers
  const executeAiEnhance = async (mode: "HASTAG" | "DIRECTOR" | "FORMAL") => {
    if (!composeText.trim()) {
      showToast("ALERT: Draft text field must be occupied before applying AI acceleration.");
      return;
    }

    setAiLoading(true);
    // Simulate smart high-velocity AI processing
    setTimeout(() => {
      let enhanced = composeText;
      if (mode === "HASTAG") {
        enhanced = `${composeText}\n\n#NexoraX #DigitalNexoraXty #EnterpriseEdge #NexoraXSystem #ActiveSecurity`;
      } else if (mode === "DIRECTOR") {
        enhanced = `[INSTITUTIONAL DIRECTIVE] ${composeText} Bypassing legacy low-velocity grids. Structured under the direct authority of Director Lead Director. performance targets engaged.`;
      } else if (mode === "FORMAL") {
        enhanced = `Notice to Enterprise Partners: ${composeText} All net settlements are finalized through Secure Payment Provider (Sort Code: Verified Payment Configuration, Account: Secure Billing Provider). Verification completed.`;
      }

      setComposeText(enhanced);
      setAiLoading(false);
      showToast("SUCCESS: AI Content Core finished optimization cycle.");
    }, 1200);
  };

  // Post Submission (Creates instantly or schedules)
  const handleCreatePost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!composeText.trim()) {
      showToast("ERROR: Post body text cannot be empty.");
      return;
    }
    if (selectedPlatforms.length === 0) {
      showToast("ERROR: At least one target social platform must be active.");
      return;
    }

    const postDate = isInstantPost
      ? new Date().toISOString()
      : new Date(scheduleTime || Date.now() + 86450000).toISOString();

    const newPost: ScheduledPost = {
      id: `SCHED-${Date.now()}`,
      content: composeText,
      platforms: selectedPlatforms,
      scheduledTime: postDate,
      status: isInstantPost ? "SENT" : "SCHEDULED",
      mediaUrl: mediaUrl || undefined,
      timestamp: new Date().toISOString()
    };

    const updated = [newPost, ...scheduledList];
    saveScheduledList(updated);

    // Call direct production automated Ayrshare compliance outbox logging
    selectedPlatforms.forEach((plat) => {
      MasterAutomationEngine.logAutomatedSocialBroadcast(plat, composeText, isInstantPost ? 200 : 202);
    });

    // Commit a developer endpoint log
    const newLog: DeveloperLog = {
      timestamp: new Date().toISOString(),
      method: "POST",
      endpoint: isInstantPost ? "/api/post" : "/api/post/schedule",
      status: 200,
      response: JSON.stringify(
        {
          status: "success",
          post_id: newPost.id,
          distribution_targets: selectedPlatforms,
          time_committed_utc: postDate,
          guardian_verification: "SECURE_REPL_LOCKED"
        },
        null,
        2
      )
    };
    saveDevLogs([newLog, ...devLogs]);

    setComposeText("");
    setMediaUrl("");
    setScheduleTime("");
    showToast(
      isInstantPost
        ? "SUCCESS: Post broadcast successfully to Ayrshare distributed API."
        : "SUCCESS: Post scheduled and committed to the Chronos Ledger."
    );
  };

  // Delete/Cancel Scheduled post
  const handleCancelPost = (id: string) => {
    const updated = scheduledList.filter((post) => post.id !== id);
    saveScheduledList(updated);
    showToast("SUCCESS: Scheduled social release has been removed from ledger.");
  };

  // Generate a brand new API key
  const handleRegenerateApiKey = () => {
    const rand = Math.floor(1000 + Math.random() * 9000);
    setApiKey(`NEX_AYR_LIVE_SEC_887095_X_KEY_${rand}`);
    showToast("SUCCESS: Brand new Ayrshare Secure API Token generated.");
  };

  // Execute interactive API Playground requests
  const handlePlaygroundSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsApiLoading(true);

    setTimeout(() => {
      try {
        const parsed = JSON.parse(apiPayload);
        if (apiEndpoint === "/api/post") {
          setApiResponse(
            JSON.stringify(
              {
                success: true,
                message: "Ayrshare Simulation Hook fully synchronized with Hetzner cloud framework.",
                data: {
                  id: `ayr_sim_${Date.now()}`,
                  post: parsed.post || "",
                  platforms: parsed.platforms || [],
                  mediaUrls: parsed.mediaUrls || [],
                  analytics: {
                    status: "Enroute",
                    latency: "0.8s",
                    verificationHash: "SHA-256-NEXORAX-AUTHENTIC"
                  }
                }
              },
              null,
              2
            )
          );
        } else if (apiEndpoint === "/api/analytics") {
          setApiResponse(
            JSON.stringify(
              {
                success: true,
                profileId: profileKey,
                metrics: {
                  linkedin: { clicks: 1205, impressions: 24000, profileVisits: 840 },
                  twitter: { clicks: 3410, retweets: 450, impressions: 56000 },
                  facebook: { clicks: 541, impressions: 12090 }
                },
                serverHostIp: "5.161.42.190"
              },
              null,
              2
            )
          );
        } else {
          setApiResponse(
            JSON.stringify(
              {
                success: true,
                endpointCalled: apiEndpoint,
                timestamp: new Date().toISOString(),
                state: "ACTIVE_MONITORING_ONLINE"
              },
              null,
              2
            )
          );
        }
      } catch (err: any) {
        setApiResponse(
          JSON.stringify(
            {
              error: "BAD REQUEST FORMAT",
              message: err.message,
              status: 400
            },
            null,
            2
          )
        );
      }
      setIsApiLoading(false);
      showToast("SANDBOX: API simulation compiled and executed successfully.");
    }, 1000);
  };

  // Dynamic platform connection toggle
  const toggleSystemIntegration = (platformKey: keyof typeof integrationsFullState) => {
    const target = integrationsFullState[platformKey];
    const isNowEnabled = !target.enabled;

    setIntegrationsFullState({
      ...integrationsFullState,
      [platformKey]: {
        ...target,
        enabled: isNowEnabled,
        account: isNowEnabled
          ? platformKey === "instagram"
            ? "@NexoraxVisual"
            : platformKey === "youtube"
            ? "Nexorax Media Core"
            : target.account
          : "Not Linked"
      }
    });

    const platformStr = String(platformKey);

    const newLog: DeveloperLog = {
      timestamp: new Date().toISOString(),
      method: "PATCH",
      endpoint: `/api/profiles/channel/${platformStr}`,
      status: 200,
      response: JSON.stringify({
        channel: platformStr,
        connected: isNowEnabled,
        profile_sync_hash: "GUARDIAN_SECURE_AUTH_COMPLETED"
      })
    };
    saveDevLogs([newLog, ...devLogs]);

    showToast(
      isNowEnabled
        ? `CONNECTED: ${platformStr.toUpperCase()} Ayrshare pipeline enabled.`
        : `DISCONNECTED: ${platformStr.toUpperCase()} system access revoked.`
    );
  };

  // Recharts high fidelity dummy metrics
  const analyticsGraphData = [
    { date: "May 19", xClicks: 1200, lClicks: 800, fbClicks: 300 },
    { date: "May 20", xClicks: 1450, lClicks: 950, fbClicks: 400 },
    { date: "May 21", xClicks: 1900, lClicks: 1200, fbClicks: 480 },
    { date: "May 22", xClicks: 1650, lClicks: 1550, fbClicks: 520 },
    { date: "May 23", xClicks: 2340, lClicks: 1890, fbClicks: 610 },
    { date: "May 24", xClicks: 2110, lClicks: 2100, fbClicks: 590 },
    { date: "May 25", xClicks: 3410, lClicks: 2855, fbClicks: 742 }
  ];

  return (
    <div className="flex flex-col w-full h-full bg-[#030611]/80 backdrop-blur-md border border-cyan-500/30 rounded-[2rem] overflow-hidden shadow-2xl relative font-sans">
      
      {/* Upper Title Hub */}
      <div className="border-b border-cyan-900/40 p-6 md:p-8 shrink-0 bg-[#060a17]/90 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-slate-400 font-serif italic text-xs tracking-wider mb-2">
            "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds."
          </p>
          <div className="flex items-center gap-3">
            <Share2 className="w-8 h-8 text-cyan-400 drop-shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
            <h2 className="text-2xl md:text-3xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
              NEXORAX <span className="logo-gradient-text">SOCIAL ENGINE</span>
              <span className="text-[9px] font-mono border border-cyan-500/20 bg-cyan-950/40 text-cyan-400 px-2.5 py-1 rounded-full font-black ml-2 tracking-widest hidden sm:inline-block">
                AYRSHARE LIVE INTEGRATOR
              </span>
            </h2>
          </div>
          <p className="text-slate-400 font-mono text-[11px] leading-relaxed uppercase tracking-wider mt-1.5 font-bold">
            API-first social media syndication & scheduling gateway • Target Hetzner Cloud:{" "}
            <span className="text-cyan-400">5.161.42.190</span>
          </p>
        </div>

        {/* Action Shortcuts */}
        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-2 bg-slate-900/40 border border-slate-800 rounded px-3 py-1.5 font-mono text-[10px] text-slate-400">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
            HETZNER VPS LOGS SYNC: ONLINE
          </div>
          <button
            onClick={() => setActivePanel("api")}
            className="flex items-center gap-1.5 px-4 py-2 border border-[#e840f3]/40 hover:bg-[#e840f3]/20 bg-transparent rounded-lg text-xs font-mono font-bold text-[#f574fe] transition-all hover:scale-[1.03]"
          >
            <Code2 className="w-3.5 h-3.5" />
            API PLAYGROUND
          </button>
        </div>
      </div>

      {/* Main Grid Framework */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        
        {/* Left Hand Section Selector Panel */}
        <nav className="w-full lg:w-72 bg-[#050813] border-b lg:border-b-0 lg:border-r border-cyan-900/30 p-4 space-y-2 shrink-0 overflow-y-auto custom-scrollbar flex flex-row lg:flex-col lg:space-x-0 space-x-2 gap-2 justify-start">
          {[
            { id: "viralswarm", label: "VIRAL SWARM DISPATCH", sub: "10,000 Social Nodes", icon: <Share2 className="w-4 h-4" /> },
            { id: "profile", label: "EXECUTIVE PORTFOLIO", sub: "Director Authority Node", icon: <UserCheck className="w-4 h-4" /> },
            { id: "composer", label: "POST COMPOSER", sub: "Draft / Dispatch Posts", icon: <Send className="w-4 h-4" /> },
            { id: "calendar", label: "SOCIAL PLANNER", sub: "Interactive Calendar", icon: <Calendar className="w-4 h-4" /> },
            { id: "analytics", label: "LIVE ANALYTICS", sub: "Conversion & Reach Metrics", icon: <TrendingUp className="w-4 h-4" /> },
            { id: "integrations", label: "LINKED PLATFORMS", sub: "Profile Handshakes", icon: <PlusCircle className="w-4 h-4" /> },
            { id: "api", label: "DEVELOPER CORE", sub: "Ayrshare Sandbox API Keys", icon: <Key className="w-4 h-4" /> }
          ].map((panel) => (
            <button
              key={panel.id}
              onClick={() => setActivePanel(panel.id as any)}
              className={`w-full text-left p-3.5 rounded-xl transition-all border flex items-center gap-3 tracking-wider group shrink-0 ${
                activePanel === panel.id
                  ? "bg-cyan-950/40 border-cyan-500/45 text-white shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                  : "bg-transparent border-transparent text-slate-400 hover:text-white hover:bg-slate-900/30"
              }`}
            >
              <div
                className={`p-2 rounded-lg shrink-0 transition-colors ${
                  activePanel === panel.id ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-900 text-slate-500 group-hover:text-slate-300"
                }`}
              >
                {panel.icon}
              </div>
              <div className="hidden lg:block leading-tight uppercase font-mono">
                <div className="text-xs font-black">{panel.label}</div>
                <div className="text-[9px] text-slate-500 mt-0.5">{panel.sub}</div>
              </div>
            </button>
          ))}

          {/* Infrastructure Metrics Status Box */}
          <div className="hidden lg:block pt-6 border-t border-cyan-950/40 mt-auto">
            <div className="bg-[#02040a] p-4 rounded-2xl border border-slate-900 space-y-3 font-mono">
              <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">NEXORAX ROUTER METRICS</span>
              <div className="space-y-2 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-500">GATEWAY IP</span>
                  <span className="text-cyan-400 font-bold">5.161.42.190</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">AYRSHARE APP</span>
                  <span className="text-emerald-400 font-bold">CONNECTED</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold">ZAKAT LEVY (1%)</span>
                  <span className="text-[#e840f3] font-bold">SYS AUTOMATED</span>
                </div>
              </div>
              
              <div className="pt-2 border-t border-slate-900 text-[9px] text-slate-500 leading-none">
                CloudSync-02 ENCRYPTED CONNECTION
              </div>
            </div>
          </div>
        </nav>

        {/* Right Hand Operational Screen Viewport */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto custom-scrollbar bg-[#02050c]/95 relative">
          
          <AnimatePresence mode="wait">
            
            {/* VIEW 1: COMSPOSER PANEL */}
            {activePanel === "composer" && (
              <motion.div
                key="composer"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-cyan-950 pb-4 gap-4">
                  <div>
                    <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                      <Send className="w-5 h-5 text-cyan-400" /> COMPOSER CONSOLE
                    </h3>
                    <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                      Release simultaneous digital briefings onto connected social protocols
                    </p>
                  </div>

                  {/* Dispatch Mode Toggle switches */}
                  <div className="flex items-center gap-3 bg-[#050914] border border-cyan-950 p-1 rounded-xl font-mono text-xs">
                    <button
                      type="button"
                      onClick={() => setIsInstantPost(true)}
                      className={`px-4 py-2 rounded-lg font-bold transition-all ${
                        isInstantPost ? "bg-cyan-500/20 border border-cyan-500/30 text-white" : "text-slate-500 hover:text-slate-300"
                      }`}
                    >
                      BCAST INSTANTLY
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsInstantPost(false)}
                      className={`px-4 py-2 rounded-lg font-bold transition-all ${
                        !isInstantPost ? "bg-[#e840f3]/20 border border-[#e840f3]/30 text-white" : "text-slate-500 hover:text-slate-300"
                      }`}
                    >
                      CHRONOS SCHEDULER
                    </button>
                  </div>
                </div>

                {/* Form Elements */}
                <form onSubmit={handleCreatePost} className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
                  
                  {/* Composer Input Left Side (7 columns) */}
                  <div className="xl:col-span-7 space-y-6">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center font-mono text-xs text-slate-500 uppercase tracking-wider">
                        <span>POST BODY CONTENT (MD COMPATIBLE)</span>
                        <span className={`${composeText.length > 250 ? "text-amber-400 font-bold" : "text-slate-500"}`}>
                          {composeText.length} / 280
                        </span>
                      </div>
                      <textarea
                        value={composeText}
                        onChange={(e) => setComposeText(e.target.value)}
                        placeholder="Draft your system release here..."
                        className="w-full h-44 bg-[#030611] border border-cyan-950 rounded-2xl p-4 font-sans text-sm text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 transition-all custom-scrollbar"
                      />
                    </div>

                    {/* AI Acceleration Tools Panel */}
                    <div className="bg-[#040817] border border-cyan-900/30 rounded-2xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" />
                        <span className="font-mono text-xs font-black text-cyan-400 uppercase tracking-widest">
                          GEMINI AI ACCELERATION PROTOCOL
                        </span>
                      </div>
                      <p className="text-slate-400 text-[10px] font-sans uppercase tracking-widest leading-relaxed">
                        Authorize intelligent neural algorithms to optimize context, sentiment values, and format.
                      </p>
                      <div className="flex flex-wrap gap-2.5 pt-1">
                        <button
                          type="button"
                          disabled={aiLoading}
                          onClick={() => executeAiEnhance("HASTAG")}
                          className="px-3.5 py-1.5 bg-cyan-950/40 hover:bg-cyan-900/40 border border-cyan-500/30 text-cyan-100 rounded-lg font-mono text-[10px] uppercase font-bold tracking-widest transition-all"
                        >
                          {aiLoading ? "ENGINE ENGAGED..." : "ADD ACCELERATOR HASHTAGS"}
                        </button>
                        <button
                          type="button"
                          disabled={aiLoading}
                          onClick={() => executeAiEnhance("DIRECTOR")}
                          className="px-3.5 py-1.5 bg-purple-950/40 hover:bg-purple-900/40 border border-[#e840f3]/30 text-purple-100 rounded-lg font-mono text-[10px] uppercase font-bold tracking-widest transition-all"
                        >
                          DIRECTOR DIRECTIVE TONE
                        </button>
                        <button
                          type="button"
                          disabled={aiLoading}
                          onClick={() => executeAiEnhance("FORMAL")}
                          className="px-3.5 py-1.5 bg-blue-950/40 hover:bg-blue-900/40 border border-blue-500/30 text-blue-100 rounded-lg font-mono text-[10px] uppercase font-bold tracking-widest transition-all"
                        >
                          NexoraX Formal Release
                        </button>
                      </div>
                    </div>

                    {/* Meta Values (Media & Date) */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Media URL Box */}
                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          MEDIA ASSET URL (IMAGE / GIF / MP4)
                        </label>
                        <input
                          type="text"
                          value={mediaUrl}
                          onChange={(e) => setMediaUrl(e.target.value)}
                          placeholder="https://images.unsplash.com/..."
                          className="w-full bg-[#030611] border border-cyan-950 rounded-xl px-4 py-3 font-mono text-xs text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 transition-all"
                        />
                      </div>

                      {/* Chronos Calendar Input */}
                      <div className="space-y-2">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          {isInstantPost ? "BROADCAST RELEASE STATE" : "CHRONOS TARGET UTC DATE & TIME"}
                        </label>
                        <input
                          type="datetime-local"
                          disabled={isInstantPost}
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full bg-[#030611] border border-cyan-950 rounded-xl px-4 py-3 font-mono text-xs text-white disabled:opacity-40 focus:outline-none focus:border-cyan-500 transition-all uppercase"
                        />
                      </div>
                    </div>

                    {/* Commit Trigger Button */}
                    <button
                      type="submit"
                      className={`w-full py-4 rounded-xl font-mono text-xs font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 border shadow-lg ${
                        isInstantPost
                          ? "bg-cyan-500 text-black hover:bg-cyan-400 border-transparent shadow-cyan-950/40"
                          : "bg-[#e840f3] text-white hover:bg-[#d035db] border-transparent shadow-purple-950/40"
                      }`}
                    >
                      <Send className="w-4 h-4" />
                      {isInstantPost
                        ? "EXECUTE IMMEDIATE AYRSHARE DISPATCH"
                        : "COMMIT SCHEDULED SOCIO-RELEASE MANIFEST"}
                    </button>
                  </div>

                  {/* Right Column: Platform Connections & Preview (5 cols) */}
                  <div className="xl:col-span-5 space-y-6">
                    {/* Channel Selection Matrix */}
                    <div className="bg-[#040817] border border-cyan-950 rounded-2xl p-6 space-y-4">
                      <span className="block font-mono text-xs font-black text-white uppercase tracking-widest border-b border-cyan-950 pb-2">
                        AIRSHARE TRANSIT DECK
                      </span>
                      <div className="space-y-2.5">
                        {[
                          { id: "linkedin", label: "LinkedIn Connect", icon: <Linkedin className="w-4 h-4 text-blue-400" /> },
                          { id: "twitter", label: "X / Twitter Route", icon: <Twitter className="w-4 h-4 text-slate-100" /> },
                          { id: "facebook", label: "Facebook Page", icon: <Facebook className="w-4 h-4 text-blue-600" /> },
                          { id: "reddit", label: "Reddit Subreddit", icon: <Globe className="w-4 h-4 text-orange-400" /> }
                        ].map((plat) => {
                          const config = integrationsFullState[plat.id as keyof typeof integrationsFullState];
                          return (
                            <button
                              key={plat.id}
                              type="button"
                              onClick={() => config.enabled && togglePlatform(plat.id)}
                              disabled={!config.enabled}
                              className={`w-full p-3.5 rounded-xl border font-mono text-xs flex items-center justify-between transition-all select-none ${
                                !config.enabled
                                  ? "opacity-30 bg-[#020409] border-slate-900 text-slate-600 cursor-not-allowed"
                                  : selectedPlatforms.includes(plat.id)
                                  ? "bg-cyan-950/30 border-cyan-500/50 text-white"
                                  : "bg-black/40 border-slate-900 text-slate-400 hover:border-slate-800"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {plat.icon}
                                <span className="font-bold uppercase">{plat.label}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {!config.enabled ? (
                                  <span className="text-[8px] border border-red-500/20 px-2 py-0.5 rounded bg-red-950/20 text-red-500 font-bold">
                                    NOT SYNCED
                                  </span>
                                ) : selectedPlatforms.includes(plat.id) ? (
                                  <span className="text-[8px] border border-cyan-500/30 px-2 py-0.5 rounded bg-cyan-900/30 text-cyan-400 font-bold">
                                    ACTIVE ROUTE
                                  </span>
                                ) : (
                                  <span className="text-[8px] border border-slate-800 px-2 py-0.5 rounded bg-slate-950/40 text-slate-500">
                                    STANDBY
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Integrated Mobile Preview */}
                    <div className="bg-[#040817] border border-cyan-950 rounded-2xl overflow-hidden font-sans">
                      <div className="bg-cyan-950/20 px-6 py-3 border-b border-cyan-950/40 flex items-center justify-between">
                        <span className="font-mono text-[10px] uppercase font-black tracking-widest text-[#00c3ff]">
                          MULTIPLEX MEDIA PREVIEW
                        </span>
                        <div className="flex gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        </div>
                      </div>

                      <div className="p-6 space-y-4 bg-black/40">
                        {/* Header details */}
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-900 border border-cyan-500/20 flex items-center justify-center font-mono text-cyan-400 text-xs font-black">
                            NX
                          </div>
                          <div>
                            <span className="block text-xs font-black text-white uppercase tracking-wide">
                              NexoraX Corporation
                            </span>
                            <span className="block text-[9px] text-slate-500 font-mono">
                              PREVIEW STATE • STANDBY TO DISPATCH
                            </span>
                          </div>
                        </div>

                        {/* Text section */}
                        <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap min-h-12 border-l border-cyan-500/25 pl-3">
                          {composeText || "Draft input is currently empty. Initialize text in the console to evaluate preview."}
                        </p>

                        {/* Media Box */}
                        {mediaUrl && (
                          <div className="aspect-video w-full rounded-lg overflow-hidden border border-cyan-950 bg-slate-950 relative">
                            <img
                              src={mediaUrl}
                              alt="Multiplex attachment preview"
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="absolute top-2 right-2 px-2 py-1 rounded bg-black/80 font-mono text-[8px] text-slate-400 border border-slate-800">
                              MEDIA ATTACHED
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>

                </form>
              </motion.div>
            )}

            {/* VIEW 2: SOCIAL PLANNER / CALENDAR */}
            {activePanel === "calendar" && (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-cyan-400" /> SOCIAL SCHEDULES LEDGER
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Direct visual queue detailing scheduled posts enroute to social node connections
                  </p>
                </div>

                {/* Queue Lists */}
                <div className="space-y-4">
                  {scheduledList.length === 0 ? (
                    <div className="border border-dashed border-slate-900 bg-[#030611] text-center py-16 rounded-[1.5rem] space-y-4">
                      <p className="font-mono text-xs text-slate-500 uppercase tracking-widest font-bold">
                        [ NO ENROUTE SOCIAL TRANSMISSIONS DETECTED ]
                      </p>
                      <button
                        onClick={() => setActivePanel("composer")}
                        className="px-6 py-2 border border-cyan-500/40 bg-cyan-950/20 text-cyan-400 rounded-lg text-xs font-mono font-bold uppercase tracking-widest hover:bg-cyan-900/40 transition-all"
                      >
                        COMPILE NEW BRIEFING
                      </button>
                    </div>
                  ) : (
                    scheduledList.map((post) => (
                      <div
                        key={post.id}
                        className="bg-[#040817] border border-cyan-950/80 hover:border-cyan-500/30 rounded-2xl p-6 transition-all shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-6 items-center"
                      >
                        {/* Status Label (3 cols) */}
                        <div className="lg:col-span-3 space-y-2.5 font-mono">
                          <div className="flex items-center gap-2.5">
                            <Clock className="w-3.5 h-3.5 text-cyan-400" />
                            <span className="text-[10px] text-slate-500 uppercase font-black">TARGET DATE (UTC)</span>
                          </div>
                          <div className="text-white text-xs font-bold bg-[#01040a] px-3 py-1.5 rounded-lg border border-slate-900 uppercase">
                            {new Date(post.scheduledTime).toLocaleString("en-US", {
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false
                            })}
                          </div>

                          <div className="flex gap-1.5 pt-1">
                            {post.platforms.map((p) => (
                              <span
                                key={p}
                                className="text-[8px] font-bold uppercase px-2 py-0.5 rounded border border-cyan-500/20 bg-cyan-950/30 text-cyan-300"
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Content text (6 cols) */}
                        <div className="lg:col-span-6 space-y-2">
                          <p className="text-xs text-slate-300 font-sans leading-relaxed">
                            {post.content}
                          </p>
                          {post.mediaUrl && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-cyan-400 uppercase font-bold">
                              • MEDIA BUFFERED: {post.mediaUrl.slice(0, 30)}...
                            </span>
                          )}
                        </div>

                        {/* Control Actions (3 cols) */}
                        <div className="lg:col-span-3 flex lg:justify-end gap-3 font-mono">
                          {post.status === "SCHEDULED" ? (
                            <>
                              <button
                                onClick={() => {
                                  // Instantly send
                                  const list = scheduledList.map((p) =>
                                    p.id === post.id ? { ...p, status: "SENT" as const } : p
                                  );
                                  saveScheduledList(list);
                                  showToast("SUCCESS: Scheduled social release forced to broadcast instantly.");
                                }}
                                className="px-3 py-2 bg-cyan-950/40 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-900/40 text-[10px] font-bold uppercase rounded-lg transition-all"
                              >
                                FORCE DISPATCH
                              </button>
                              <button
                                onClick={() => handleCancelPost(post.id)}
                                className="p-2 border border-red-500/30 text-red-400 hover:bg-red-950/20 rounded-lg transition-all"
                                title="Cancel and Delete release"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold px-2.5 py-1 rounded bg-emerald-950/20 border border-emerald-500/20 text-emerald-400 uppercase">
                                SUCCESS DISPATCHED
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Simulated Weekly Visual Scheduler Calendar View Grid */}
                <div className="bg-[#030611] border border-cyan-950 rounded-[1.5rem] p-6 space-y-4">
                  <span className="block font-mono text-xs font-black text-white uppercase tracking-widest border-b border-cyan-950 pb-2">
                    WEEKLY SOCIO-PLANNER MATRIX GRID
                  </span>
                  
                  <div className="grid grid-cols-2 md:grid-cols-7 gap-3 font-mono text-[10px] uppercase">
                    {[
                      { day: "MON 25", tasks: 0 },
                      { day: "TUE 26", tasks: 0 },
                      { day: "WED 27", tasks: 0 },
                      { day: "THU 28", tasks: 1 },
                      { day: "FRI 29", tasks: 0 },
                      { day: "SAT 30", tasks: 1 },
                      { day: "SUN 31", tasks: 2 }
                    ].map((slot) => (
                      <div
                        key={slot.day}
                        className={`p-4 rounded-xl border flex flex-col justify-between aspect-square h-24 ${
                          slot.tasks > 0
                            ? "bg-cyan-950/15 border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.1)]"
                            : "bg-slate-950/20 border-slate-900 text-slate-600"
                        }`}
                      >
                        <span className="font-bold text-slate-400">{slot.day}</span>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[9px] text-slate-500">POSTS</span>
                          <span
                            className={`text-xs font-bold ${
                              slot.tasks > 0 ? "text-cyan-400 glow-text-cyan" : "text-slate-700"
                            }`}
                          >
                            {slot.tasks}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </motion.div>
            )}

            {/* VIEW 3: LIVE ANALYTICS */}
            {activePanel === "analytics" && (
              <motion.div
                key="analytics"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="border-b border-cyan-950 pb-4 flex justify-between items-center">
                  <div>
                    <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-cyan-400" /> LIVE ENGAGEMENT ANALYTICS
                    </h3>
                    <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                      Aggregated click & conversion stats fetched directly from connected social APIs
                    </p>
                  </div>
                  
                  <span className="font-mono text-[10px] border border-cyan-500/30 bg-cyan-950/20 px-3 py-1 rounded text-cyan-400">
                    REALTIME CORRECTIONS ACTIVE
                  </span>
                </div>

                {/* Analytics summary metric cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 font-mono text-left">
                  <div className="bg-[#040817] border border-cyan-950 rounded-2xl p-5 relative overflow-hidden">
                    <span className="block text-[10px] text-slate-500 uppercase tracking-widest font-bold">TOTAL BROADCAST AUDIENCE</span>
                    <span className="block text-2xl font-black text-white glow-text-cyan tracking-tight mt-2">48,450+</span>
                    <span className="block text-[9px] text-[#00c3ff] mt-1 uppercase font-bold">● FOLLOWERS ENUMERATED</span>
                  </div>
                  <div className="bg-[#040817] border border-cyan-950 rounded-2xl p-5 relative overflow-hidden">
                    <span className="block text-[10px] text-slate-500 uppercase tracking-widest font-bold">DISPATCHED LOG IMPRESSIONS</span>
                    <span className="block text-2xl font-black text-white tracking-tight mt-2">142,010</span>
                    <span className="block text-[9px] text-emerald-400 mt-1 uppercase font-bold">+18.4% WEEK-OVER-WEEK</span>
                  </div>
                  <div className="bg-[#040817] border border-[#e840f3]/20 rounded-2xl p-5 relative overflow-hidden">
                    <span className="block text-[10px] text-slate-500 uppercase tracking-widest font-bold">NexoraX Link Clicks</span>
                    <span className="block text-2xl font-black text-white text-[#f574fe] tracking-tight mt-2">6,995</span>
                    <span className="block text-[9px] text-slate-400 mt-1 uppercase font-bold">3.92% CONVERSION AVERAGE</span>
                  </div>
                  <div className="bg-[#040817] border border-cyan-950 rounded-2xl p-5 relative overflow-hidden">
                    <span className="block text-[10px] text-slate-500 uppercase tracking-widest font-bold">API KEY HIT VELOCITY</span>
                    <span className="block text-2xl font-black text-white tracking-tight mt-2">1,244</span>
                    <span className="block text-[9px] text-slate-500 mt-1 uppercase font-bold">0.8s RESPONSE AVERAGE</span>
                  </div>
                </div>

                {/* Main analytical Visual Area Chart */}
                <div className="bg-[#030611] border border-cyan-950 rounded-[1.5rem] p-6 space-y-6">
                  <span className="block font-mono text-xs font-black text-white uppercase tracking-widest border-b border-cyan-950 pb-2">
                    7-DAY AGGREGATED SOCIAL CLIICKS FLOW
                  </span>

                  <div className="w-full h-80">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <AreaChart data={analyticsGraphData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorX" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#00b0ff" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#00b0ff" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorL" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#7a00ff" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#7a00ff" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#081026" />
                        <XAxis dataKey="date" stroke="#475569" fontSize={10} fontFamily="monospace" />
                        <YAxis stroke="#475569" fontSize={10} fontFamily="monospace" />
                        <Tooltip contentStyle={{ backgroundColor: "#040817", borderColor: "#0e1833", color: "#cbd5e1", fontSize: "11px", fontFamily: "monospace" }} />
                        <Area type="monotone" dataKey="xClicks" name="X (Twitter) clicks" stroke="#00b0ff" fillOpacity={1} fill="url(#colorX)" strokeWidth={2} />
                        <Area type="monotone" dataKey="lClicks" name="LinkedIn clicks" stroke="#7a00ff" fillOpacity={1} fill="url(#colorL)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

              </motion.div>
            )}

            {/* VIEW 4: PLATFORM INTEGRATION MANAGER */}
            {activePanel === "integrations" && (
              <motion.div
                key="integrations"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <Layers className="w-5 h-5 text-cyan-400" /> INTEGRATED PLATFORMS DIRECTORY
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Establish visual profile handshakes with connected global API configurations
                  </p>
                </div>

                {/* Grid Layout of active targets */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-mono text-left">
                  
                  {/* Option Grid */}
                  {[
                    { key: "linkedin", name: "LINKEDIN ENTERPRISE", icon: <Linkedin className="w-6 h-6 text-blue-400" />, desc: "Publish B2B directives, leadership statements, and capital forecasts simultaneously." },
                    { key: "twitter", name: "X / TWITTER NET", icon: <Twitter className="w-6 h-6 text-slate-100" />, desc: "Push micro-releases, live thread integrations, and network announcements instantly." },
                    { key: "facebook", name: "FACEBOOK GROUP", icon: <Facebook className="w-6 h-6 text-blue-600" />, desc: "Deploy strategic updates onto structured business hubs across the global matrix." },
                    { key: "instagram", name: "INSTAGRAM VISUAL", icon: <Instagram className="w-6 h-6 text-pink-400" />, desc: "Broaden visual brand scope with media asset logs and design matrices." },
                    { key: "youtube", name: "YOUTUBE PROTOCOL", icon: <Youtube className="w-6 h-6 text-red-500" />, desc: "Route video and voice generation outputs securely across external feeds." },
                    { key: "reddit", name: "REDDIT COMMUNITIES", icon: <Globe className="w-6 h-6 text-orange-400" />, desc: "Disseminate technical blueprints onto key subnet discussions and panels." }
                  ].map((chan) => {
                    const data = integrationsFullState[chan.key as keyof typeof integrationsFullState];
                    return (
                      <div
                        key={chan.key}
                        className={`bg-[#040817] p-6 rounded-[1.5rem] border transition-all flex flex-col justify-between shadow-lg h-full relative ${
                          data.enabled ? "border-cyan-500/30" : "border-slate-900 opacity-60"
                        }`}
                      >
                        <div>
                          {/* Top row */}
                          <div className="flex justify-between items-center mb-5">
                            <span className="p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                              {chan.icon}
                            </span>
                            
                            {/* Toggle Switch */}
                            <button
                              onClick={() => toggleSystemIntegration(chan.key as any)}
                              className={`w-12 h-6 rounded-full p-1 transition-colors relative flex items-center ${
                                data.enabled ? "bg-cyan-500" : "bg-slate-800"
                              }`}
                            >
                              <div
                                className={`w-4 h-4 rounded-full bg-black transition-transform ${
                                  data.enabled ? "translate-x-6" : "translate-x-0"
                                }`}
                              ></div>
                            </button>
                          </div>

                          <h4 className="text-white text-md font-bold uppercase tracking-wider mb-2">
                            {chan.name}
                          </h4>
                          
                          <p className="text-slate-400 text-[11px] leading-relaxed uppercase tracking-wider mb-5">
                            {chan.desc}
                          </p>
                        </div>

                        {/* Connection statistics meta */}
                        <div className="pt-4 border-t border-slate-900 space-y-2 text-[10px]">
                          <div className="flex justify-between">
                            <span className="text-slate-500">SYNC STATE:</span>
                            <span className={data.enabled ? "text-cyan-400 font-bold" : "text-slate-600 font-bold"}>
                              {data.enabled ? "ACTIVE CONNECTION" : "NOT LINKED"}
                            </span>
                          </div>
                          {data.enabled && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-slate-500">ACCOUNT NAME:</span>
                                <span className="text-white font-bold">{data.account}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">REACH ESTIMATE:</span>
                                <span className="text-cyan-400 font-bold">{data.followers}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                </div>
              </motion.div>
            )}

            {/* VIEW 5: CHANNELS & API INTEGRATION KEYS (AYRSHARE CORE PLAYGROUND) */}
            {activePanel === "api" && (
              <motion.div
                key="api"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="border-b border-cyan-950 pb-4">
                  <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                    <Key className="w-5 h-5 text-cyan-400" /> AYRSHARE DEVELOPER CORE
                  </h3>
                  <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                    Configure server integrations, manage private access keys, and evaluate API endpoints live
                  </p>
                </div>

                {/* API Credentials Card */}
                <div className="bg-[#040817] border border-cyan-950 p-6 rounded-[1.5rem] space-y-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-900 pb-4 gap-4">
                    <span className="font-mono text-xs font-black text-white uppercase tracking-widest">
                      NEXORAX API INTEGRITY CREDENTIALS
                    </span>
                    <button
                      onClick={handleRegenerateApiKey}
                      className="px-4 py-2 bg-cyan-950/40 hover:bg-cyan-900/40 border border-cyan-500/30 text-cyan-400 rounded-lg text-xs font-mono font-bold uppercase tracking-widest transition-all"
                    >
                      REGENERATE MASTER API TOKEN
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 font-mono text-xs">
                    
                    {/* API Key Box */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-slate-500">
                        <span>AYRSHARE PRIVATE MASTER KEY</span>
                        <button
                          onClick={() => setIsKeysVisible(!isKeysVisible)}
                          className="text-cyan-400 hover:underline font-bold"
                        >
                          {isKeysVisible ? "[HIDE KEYS]" : "[REVEAL KEYS]"}
                        </button>
                      </div>
                      <div className="bg-[#02040a] border border-slate-900 p-4 rounded-xl flex items-center justify-between">
                        <span className="font-bold text-slate-100">
                          {isKeysVisible ? apiKey : "••••••••••••••••••••••••••••••••••"}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(apiKey);
                            showToast("SUCCESS: API token copied to clipboard.");
                          }}
                          className="text-slate-400 hover:text-cyan-400"
                        >
                          COPY
                        </button>
                      </div>
                    </div>

                    {/* Profile ID Box */}
                    <div className="space-y-2">
                      <div className="text-slate-500">AYRSHARE PROFILE USER ID (JWT REPLICATE)</div>
                      <div className="bg-[#02040a] border border-slate-900 p-4 rounded-xl flex items-center justify-between">
                        <span className="font-bold text-slate-100">
                          {isKeysVisible ? profileKey : "••••••••••••••••••••••••••••••••••"}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(profileKey);
                            showToast("SUCCESS: Profile Key copied to clipboard.");
                          }}
                          className="text-slate-400 hover:text-cyan-400"
                        >
                          COPY
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                {/* API Interactive Simulator Sandbox */}
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
                  
                  {/* Interactive form (5 cols) */}
                  <form onSubmit={handlePlaygroundSubmit} className="xl:col-span-6 bg-[#030611] border border-cyan-950 p-6 rounded-[1.5rem] space-y-4 font-mono">
                    <span className="block text-xs font-black text-white uppercase tracking-widest border-b border-cyan-950 pb-2">
                      INTERACTIVE API PLAYGROUND
                    </span>

                    {/* Select Endpoint Router */}
                    <div className="space-y-2">
                      <label className="text-[10px] text-slate-500 font-bold uppercase">TARGET API ENDPOINT</label>
                      <select
                        value={apiEndpoint}
                        onChange={(e) => {
                          setApiEndpoint(e.target.value);
                          if (e.target.value === "/api/analytics") {
                            setApiMethod("GET");
                            setApiPayload("");
                          } else {
                            setApiMethod("POST");
                            setApiPayload(
                              JSON.stringify(
                                {
                                  post: "NexoraX Global Infrastructure achieves a consistent 0.8s Handshake standard. Commencing full scalability optimization.",
                                  platforms: ["linkedin", "twitter"],
                                  mediaUrls: ["https://images.unsplash.com/photo-1550751827-4bd374c3f58b"]
                                },
                                null,
                                2
                              )
                            );
                          }
                        }}
                        className="w-full bg-slate-950 border border-cyan-950 rounded-xl px-4 py-3 text-xs text-white uppercase focus:outline-none"
                      >
                        <option value="/api/post">POST CONTENT - /api/post</option>
                        <option value="/api/analytics">GET METRICS - /api/analytics</option>
                        <option value="/api/history">GET HISTORY - /api/history</option>
                      </select>
                    </div>

                    {/* API Payload Box */}
                    {apiMethod === "POST" && (
                      <div className="space-y-2">
                        <label className="text-[10px] text-slate-500 font-bold uppercase">REQUEST JSON PAYLOAD BODY</label>
                        <textarea
                          value={apiPayload}
                          onChange={(e) => setApiPayload(e.target.value)}
                          className="w-full h-36 bg-slate-950 border border-cyan-950 rounded-xl p-3 text-xs text-emerald-400 font-mono focus:outline-none focus:border-cyan-500 transition-all custom-scrollbar"
                        />
                      </div>
                    )}

                    {/* Sandbox execution trigger */}
                    <button
                      type="submit"
                      disabled={isApiLoading}
                      className="w-full py-3.5 bg-gradient-to-r from-cyan-900/50 to-blue-900/30 border border-cyan-500/50 hover:bg-cyan-900/40 rounded-xl font-bold text-xs text-cyan-400 uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                    >
                      {isApiLoading ? (
                        <Cpu className="w-4 h-4 text-cyan-300 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 text-cyan-400" />
                      )}
                      {isApiLoading ? "SIMULATING DISPATCH ROUTING..." : "EXECUTE SANDBOX CALL"}
                    </button>
                  </form>

                  {/* API Response display console (7 cols) */}
                  <div className="xl:col-span-6 bg-black/40 border border-cyan-950 p-6 rounded-[1.5rem] flex flex-col font-mono justify-between">
                    <div className="w-full">
                      <div className="flex justify-between items-center border-b border-cyan-950 pb-2 mb-4">
                        <span className="text-xs font-black text-white uppercase tracking-widest">
                          SANDBOX RESPONSE CONSOLE
                        </span>
                        <span className="text-[8px] border border-cyan-500/20 px-2.5 py-1 rounded bg-cyan-950/30 text-cyan-400 uppercase">
                          HETZNER PORT 3000
                        </span>
                      </div>
                      <pre className="text-xs text-slate-300 leading-relaxed overflow-x-auto whitespace-pre-wrap max-h-52 custom-scrollbar bg-slate-950/80 border border-slate-900 p-4 rounded-xl min-h-[140px]">
                        {apiResponse || "Execute playground parameters above to observe raw JSON telemetry."}
                      </pre>
                    </div>

                    {/* Developer Code integration snippets */}
                    <div className="pt-6 border-t border-slate-900 mt-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] text-slate-500 uppercase font-black">
                          INTEGRATION BLUEPRINT SNIPPET
                        </span>
                        <div className="flex gap-2 text-[9px] text-slate-400 font-bold uppercase">
                          <span className="text-cyan-400 border border-cyan-500/30 px-2 py-0.5 rounded bg-cyan-950/10">NODEJS</span>
                          <span className="hover:text-cyan-300 cursor-pointer">CURL</span>
                          <span className="hover:text-cyan-300 cursor-pointer">PYTHON</span>
                        </div>
                      </div>

                      <pre className="text-[9px] bg-slate-950 text-slate-400 p-3 rounded-lg overflow-x-auto font-mono scrollbar-none leading-relaxed border border-slate-900">
{`const AyrShare = require('ayrshare-node');
const social = new AyrShare('${apiKey}');

async function distributeBriefing() {
  const result = await social.post({
    post: "Blueprints updated successfully on SecurePayment Node.",
    platforms: ["linkedin", "twitter"],
    profileKeys: ["${profileKey}"]
  });
  console.log(result);
}`}
                      </pre>
                    </div>

                  </div>

                </div>

                {/* Developer Log Output */}
                <div className="bg-[#030611] border border-cyan-950 rounded-[1.5rem] p-6 space-y-4 font-mono">
                  <div className="flex justify-between items-center border-b border-cyan-950 pb-2">
                    <span className="text-xs font-black text-white uppercase tracking-widest">
                      SYSTEM ACCESS TRANSACTION LOG
                    </span>
                    <button
                      onClick={() => {
                        saveDevLogs([]);
                        showToast("SUCCESS: Transaction logs cleared.");
                      }}
                      className="text-slate-500 hover:text-cyan-400 text-[10px] font-bold uppercase"
                    >
                      CLEAR RECORDS
                    </button>
                  </div>

                  <div className="space-y-2.5 max-h-48 overflow-y-auto custom-scrollbar">
                    {devLogs.length === 0 ? (
                      <div className="text-center py-6 text-[10px] text-slate-600">
                        [ SYSTEM TRANSIENT BUFFER IS EMPTY ]
                      </div>
                    ) : (
                      devLogs.map((log, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center text-[10px] p-2 bg-black/40 border border-slate-900 rounded-lg text-slate-500"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-[#00c3ff] font-bold">[{log.method}]</span>
                            <span className="text-white font-bold">{log.endpoint}</span>
                            <span className="text-slate-600">|</span>
                            <span className="text-emerald-400">STATUS: {log.status}</span>
                          </div>
                          <span>{new Date(log.timestamp).toLocaleTimeString()}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </motion.div>
            )}

            {/* VIEW 6: EXECUTIVE LINKEDIN PROFILE OVERHAUL */}
            {activePanel === "profile" && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-b border-cyan-950 pb-4 gap-4">
                  <div>
                    <h3 className="font-sans font-black text-xl text-white uppercase tracking-wider flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-cyan-400" /> EXECUTIVE AUTHORITY COMMAND
                    </h3>
                    <p className="text-slate-400 text-xs font-mono uppercase tracking-widest mt-1">
                      Overhaul delivery-focused templates to showcase Director & NexoraX team authority
                    </p>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => {
                        // Reset to Director default
                        setExecutiveName("Lead Director");
                        setExecutiveHeadline("Director & Founder | Senior Web Architect | NexoraX Digital Enterprise | AI Systems & Voice Matrix");
                        setExecutiveAbout("Architecting the next generation of global digital infrastructure. As the Director of NexoraX Digital Enterprise, I bridge the gap between complex web architecture, NexoraX AI voice automation, and hyper-conversion UI/UX design. Our mission is to engineer autonomous, high-velocity digital ecosystems that provide unparalleled market dominance for government and enterprise-level clients.");
                        setExecutiveExperience([
                          {
                            id: "exp-1",
                            title: "Founder & Director",
                            company: "NexoraX Digital Enterprise",
                            duration: "2024 - Present",
                            description: "Leading the development of the NexoraX Digital OS, focusing on AI-driven voice telephony routing, global reconnaissance intelligence nodes, and NexoraX charitable foundations."
                          },
                          {
                            id: "exp-2",
                            title: "Senior Web Architect & Design Systems Lead",
                            company: "NexoraX Digital",
                            duration: "2022 - 2024",
                            description: "Architecting mission-critical, glassmorphic design systems and automated intelligence engines that optimize customer acquisition through high-conversion UI/UX and voice matrix integration."
                          }
                        ]);
                        showToast("RESET: Authority matrix returned to Director specifications.");
                      }}
                      className="px-3 py-1.5 border border-slate-800 hover:border-slate-700 bg-transparent rounded-lg text-xs font-mono font-bold text-slate-400 transition-all cursor-pointer"
                    >
                      RESET DEFAULT
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setSavingProfile(true);
                        setTimeout(() => {
                          localStorage.setItem("NEX_EXEC_NAME", executiveName);
                          localStorage.setItem("NEX_EXEC_HEADLINE", executiveHeadline);
                          localStorage.setItem("NEX_EXEC_ABOUT", executiveAbout);
                          localStorage.setItem("NEX_EXEC_EXP", JSON.stringify(executiveExperience));
                          setSavingProfile(false);
                          showToast("SUCCESS: Profile matrix mirrored to NexoraX Resource Library via CloudSync-02.");
                          
                          // Log endpoint call
                          const newLog: DeveloperLog = {
                            timestamp: new Date().toISOString(),
                            method: "POST",
                            endpoint: "/api/profile/overhaul",
                            status: 200,
                            response: JSON.stringify({
                              status: "COMPLETED",
                              profile: "Lead Director Authority",
                              guardian_verification: "SECURE_REPL_LOCKED", 
                              auditRoute: "Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider)"
                            }, null, 2)
                          };
                          saveDevLogs([newLog, ...devLogs]);
                        }, 1000);
                      }}
                      disabled={savingProfile}
                      className="px-4 py-1.5 bg-cyan-500 text-black font-mono font-black text-xs uppercase tracking-wider rounded-lg hover:bg-cyan-400 transition-all flex items-center gap-1.5 cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.4)]"
                    >
                      {savingProfile ? (
                        <>
                          <RefreshCw className="w-3 animate-spin" />
                          MIRRORING...
                        </>
                      ) : (
                        <>
                          <Database className="w-3 h-3" />
                          MIRROR TO VAULT
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
                  {/* Left edit column (5 cols) */}
                  <div className="xl:col-span-5 space-y-6">
                    <div className="bg-[#040817] border border-cyan-950 rounded-2xl p-6 space-y-5">
                      <div className="flex items-center gap-2 border-b border-cyan-950 pb-3">
                        <Terminal className="w-4 h-4 text-cyan-400" />
                        <span className="font-mono text-xs font-black text-white uppercase tracking-wider">
                          BIOMETRIC AUTHORITY CONTROLS
                        </span>
                      </div>

                      {/* Name input */}
                      <div className="space-y-1.5">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          Executive Leader Name
                        </label>
                        <input
                          type="text"
                          value={executiveName}
                          onChange={(e) => setExecutiveName(e.target.value)}
                          className="w-full bg-black border border-cyan-950 rounded-xl px-3.5 py-2.5 font-sans text-xs text-white focus:outline-none focus:border-cyan-500 transition-all"
                        />
                      </div>

                      {/* Headline input */}
                      <div className="space-y-1.5">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          LinkedIn Professional Headline
                        </label>
                        <textarea
                          rows={2}
                          value={executiveHeadline}
                          onChange={(e) => setExecutiveHeadline(e.target.value)}
                          className="w-full bg-black border border-cyan-950 rounded-xl px-3.5 py-2.5 font-sans text-xs text-white focus:outline-none focus:border-cyan-500 transition-all resize-none"
                        />
                      </div>

                      {/* About summary input */}
                      <div className="space-y-1.5">
                        <label className="block font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                          Authority biographical abstract (About)
                        </label>
                        <textarea
                          rows={6}
                          value={executiveAbout}
                          onChange={(e) => setExecutiveAbout(e.target.value)}
                          className="w-full bg-black border border-cyan-950 rounded-xl px-3.5 py-2.5 font-sans text-xs text-slate-350 focus:outline-none focus:border-cyan-500 transition-all custom-scrollbar shrink-0"
                        />
                      </div>

                      {/* Experience Item Editor */}
                      <div className="space-y-3.5 pt-2">
                        <div className="flex items-center justify-between border-t border-cyan-950/40 pt-4">
                          <span className="font-mono text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                            Corporate Experience Matrix
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const newExp = {
                                id: `exp-${Date.now()}`,
                                title: "New Senior Position",
                                company: "NexoraX Digital Enterprise",
                                duration: "2026 - Present",
                                description: "Description and authority deployment summary."
                              };
                              setExecutiveExperience([...executiveExperience, newExp]);
                              showToast("ADDED: New strategic corporate node generated.");
                            }}
                            className="text-cyan-400 text-[10px] font-mono hover:underline uppercase flex items-center gap-1 font-bold"
                          >
                            <Plus className="w-3 h-3" /> Add Node
                          </button>
                        </div>

                        <div className="space-y-4 max-h-[220px] overflow-y-auto custom-scrollbar pr-1.5">
                          {executiveExperience.map((exp: any, index: number) => (
                            <div key={exp.id} className="p-3.5 bg-black/40 border border-cyan-950/40 rounded-xl space-y-3 relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setExecutiveExperience(executiveExperience.filter((e: any) => e.id !== exp.id));
                                  showToast("REMOVED: Strategic node pruned.");
                                }}
                                className="absolute top-2.5 right-2.5 text-slate-600 hover:text-red-400 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>

                              <div className="grid grid-cols-2 gap-2">
                                <div className="space-y-1">
                                  <span className="text-[8px] font-mono text-slate-600">TITLE</span>
                                  <input
                                    type="text"
                                    value={exp.title}
                                    onChange={(e) => {
                                      const updated = [...executiveExperience];
                                      updated[index].title = e.target.value;
                                      setExecutiveExperience(updated);
                                    }}
                                    className="w-full bg-black/60 border border-slate-900 rounded px-2 py-1 font-sans text-[10px] text-white"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <span className="text-[8px] font-mono text-slate-600">COMPANY</span>
                                  <input
                                    type="text"
                                    value={exp.company}
                                    onChange={(e) => {
                                      const updated = [...executiveExperience];
                                      updated[index].company = e.target.value;
                                      setExecutiveExperience(updated);
                                    }}
                                    className="w-full bg-black/60 border border-slate-900 rounded px-2 py-1 font-sans text-[10px] text-white"
                                  />
                                </div>
                              </div>

                              <div className="space-y-1">
                                <span className="text-[8px] font-mono text-slate-600">DURATION</span>
                                <input
                                  type="text"
                                  value={exp.duration}
                                  onChange={(e) => {
                                    const updated = [...executiveExperience];
                                    updated[index].duration = e.target.value;
                                    setExecutiveExperience(updated);
                                  }}
                                  className="w-full bg-black/60 border border-slate-900 rounded px-2 py-1 font-sans text-[10px] text-white"
                                />
                              </div>

                              <div className="space-y-1">
                                <span className="text-[8px] font-mono text-slate-600">CORE RESPONSIBILITY AND DIRECTIVES</span>
                                <textarea
                                  rows={2}
                                  value={exp.description}
                                  onChange={(e) => {
                                    const updated = [...executiveExperience];
                                    updated[index].description = e.target.value;
                                    setExecutiveExperience(updated);
                                  }}
                                  className="w-full bg-black/60 border border-slate-900 rounded px-2 py-1 font-sans text-[10px] text-stone-300 resize-none line-clamp-3"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Right live LinkedIn view (7 cols) */}
                  <div className="xl:col-span-7 space-y-6">
                    <div className="bg-[#0b1022] border border-[#22d3ee]/20 rounded-3xl overflow-hidden shadow-[0_15px_30px_rgba(0,0,0,0.5)]">
                      {/* Premium Cover Banner */}
                      <div className="h-44 bg-gradient-to-br from-[#060a17] via-[#0b1836] to-[#040816] relative overflow-hidden border-b border-cyan-950 flex items-center justify-center">
                        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent opacity-60" />
                        
                        {/* Abstract neural architecture lines */}
                        <svg className="absolute inset-0 w-full h-full opacity-15" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <pattern id="cover-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#22d3ee" strokeWidth="0.5"/>
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill="url(#cover-grid)" />
                          <circle cx="20%" cy="40%" r="3" fill="#22d3ee" />
                          <circle cx="50%" cy="30%" r="5" fill="#f574fe" />
                          <circle cx="80%" cy="70%" r="4" fill="#22d3ee" />
                          <line x1="20%" y1="40%" x2="50%" y2="30%" stroke="#22d3ee" strokeWidth="1" />
                          <line x1="50%" y1="30%" x2="80%" y2="70%" stroke="#f574fe" strokeWidth="1" />
                        </svg>

                        <div className="absolute top-4 left-6 flex items-center gap-2 font-mono text-[9px] bg-cyan-950/60 backdrop-blur-md border border-cyan-500/30 text-cyan-400 px-3 py-1 rounded-full uppercase font-bold tracking-widest">
                          <Cpu className="w-3 h-3 text-[#22d3ee] animate-pulse" />
                          NEXORAX OS INFRASTRUCTURE ACTIVE
                        </div>

                        <div className="absolute right-6 bottom-4 flex items-center gap-1 font-mono text-[8px] bg-black/40 text-slate-500 px-2 py-0.5 rounded">
                          HETZNER VPS NODE LOCKED
                        </div>

                        <div className="text-center space-y-1 select-none pointer-events-none">
                          <h4 className="text-white font-sans font-black tracking-[0.2em] uppercase text-sm">
                            NEXORAX DIGITAL ENTERPRISE
                          </h4>
                          <p className="text-[#22d3ee] font-mono text-[9px] tracking-widest uppercase">
                            0.8s LATENCY INTEGRATION • NEXORAX NEURAL SOLUTIONS
                          </p>
                        </div>
                      </div>

                      {/* Profile Card Main Body */}
                      <div className="p-8 relative">
                        {/* Bio Photo Container */}
                        <div className="absolute -top-16 left-8 flex items-end">
                          <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-cyan-900 to-purple-900 border-4 border-[#0b1022] flex items-center justify-center shadow-2xl relative group overflow-hidden">
                            <div className="absolute inset-0 bg-black/20" />
                            <UserCheck className="w-12 h-12 text-cyan-400 group-hover:scale-110 transition-transform duration-300" />
                            
                            {/* Verified Badge */}
                            <div className="absolute bottom-1 right-1 w-5 h-5 bg-cyan-400 rounded-full border-2 border-[#0b1022] flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 text-black fill-cyan-400 stroke-[3]" />
                            </div>
                          </div>
                        </div>

                        {/* Profile CTA Group */}
                        <div className="flex justify-end gap-3 font-mono text-[10px] items-center pt-2">
                          <span className="text-emerald-400 font-bold bg-emerald-950/40 border border-emerald-500/20 px-2.5 py-1 rounded-lg flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                            ACTIVE MEMBER
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              showToast("CONNECT: Contact handshake dispatched via support@nexoraxdigital.co.uk");
                            }}
                            className="px-4 py-1.5 border border-cyan-500/40 hover:bg-cyan-500/20 text-cyan-400 hover:text-white rounded-lg transition-all font-bold uppercase tracking-wider cursor-pointer font-bold"
                          >
                            CONNECT
                          </button>
                        </div>

                        {/* Title and stats block */}
                        <div className="mt-6 space-y-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-xl font-sans font-black text-white tracking-tight">
                                {executiveName}
                              </h3>
                              <span className="text-[10px] bg-purple-950/55 border border-purple-500/30 text-purple-400 px-2 py-0.5 rounded font-mono font-bold tracking-wider animate-pulse">
                                NEX_CORE_DIRECTOR
                              </span>
                            </div>

                            <p className="text-cyan-400 font-sans text-[12.5px] font-medium leading-relaxed tracking-wide mt-1.5 border-l-2 border-cyan-500/40 pl-3">
                              {executiveHeadline}
                            </p>

                            <div className="flex items-center gap-4 text-slate-500 font-mono text-[10px] mt-4 uppercase font-bold">
                              <span>Location: United Kingdom</span>
                              <span>•</span>
                              <span className="text-[#22d3ee] hover:underline cursor-pointer" onClick={() => showToast("ANCHOR DETAILS: support@nexoraxdigital.co.uk | 08000 622827")}>
                                support@nexoraxdigital.co.uk
                              </span>
                            </div>
                          </div>

                          {/* Professional about summary */}
                          <div className="pt-6 border-t border-slate-900 space-y-3">
                            <span className="block font-mono text-[10px] text-slate-400 uppercase font-black tracking-widest">
                              // EXECUTIVE SYNOPSIS (ABOUT)
                            </span>
                            <p className="text-slate-300 font-sans text-xs leading-relaxed text-justify bg-slate-950/40 p-4 rounded-xl border border-slate-900/60 max-h-48 overflow-y-auto custom-scrollbar">
                              {executiveAbout}
                            </p>
                          </div>

                          {/* Corporate nodes experiences */}
                          <div className="pt-6 border-t border-slate-900 space-y-4">
                            <span className="block font-mono text-[10px] text-slate-400 uppercase font-black tracking-widest">
                              // STRATEGIC RECONNAISSANCE NODES (EXPERIENCE)
                            </span>

                            <div className="space-y-4">
                              {executiveExperience.map((exp: any) => (
                                <div key={exp.id} className="flex gap-4 items-start font-sans">
                                  <div className="w-10 h-10 shrink-0 bg-cyan-950/20 border border-cyan-500/20 rounded-xl flex items-center justify-center text-cyan-400 font-mono text-xs font-black">
                                    NX
                                  </div>
                                  <div className="space-y-1 flex-1">
                                    <div className="flex justify-between items-start">
                                      <h4 className="text-sm font-sans font-black text-white">
                                        {exp.title}
                                      </h4>
                                      <span className="text-[10px] font-mono text-slate-500 bg-[#01040a] px-2.5 py-0.5 rounded border border-slate-900">
                                        {exp.duration}
                                      </span>
                                    </div>
                                    <p className="text-cyan-400 text-xs font-mono font-bold tracking-wide">
                                      {exp.company}
                                    </p>
                                    <p className="text-slate-400 text-[11px] leading-relaxed pt-1">
                                      {exp.description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Action Deck */}
                          <div className="pt-6 border-t border-slate-900 grid grid-cols-2 lg:grid-cols-4 gap-3 text-center text-[10px] font-mono font-bold">
                            <button
                              type="button"
                              onClick={() => {
                                const payload = {
                                  executiveName,
                                  executiveHeadline,
                                  executiveAbout,
                                  executiveExperience
                                };
                                const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = "NEXORAX_DIRECTOR_PROFILE.json";
                                a.click();
                                showToast("EXPORT: Biometric JSON manifest dispatched.");
                              }}
                              className="p-3 bg-cyan-950/20 hover:bg-cyan-100 hover:text-black border border-cyan-500/20 text-cyan-400 rounded-xl transition-all cursor-pointer"
                            >
                              JSON COGNITIVE FILE
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                showToast("PREPARE: PDF generation pipeline initialized on SecurePayment ledger.");
                              }}
                              className="p-3 bg-purple-950/20 hover:bg-purple-100 hover:text-black border border-purple-500/20 text-purple-400 rounded-xl transition-all cursor-pointer"
                            >
                              AUTHORITY DOSSIER
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(`${executiveName}\n${executiveHeadline}\n\n${executiveAbout}`);
                                showToast("COPIED: Executive biometrics placed in local active buffer.");
                              }}
                              className="p-3 bg-slate-900/60 hover:bg-white hover:text-black text-slate-300 border border-slate-800 rounded-xl transition-all cursor-pointer"
                            >
                              COPY BIO DATA
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                showToast("ZAKAT EXTRACTION: Tiered Tax 20% & 3% Community Allocation 1% checked.");
                              }}
                              className="p-3 bg-amber-950/20 hover:bg-amber-100 hover:text-black border border-amber-500/20 text-amber-500 rounded-xl transition-all cursor-pointer"
                            >
                              ZAKAT LEDGER SYNC
                            </button>
                          </div>

                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {activePanel === "viralswarm" && (
              <motion.div
                key="viralswarm"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.25 }}
                className="w-full"
              >
                <NexoraXViralSwarm />
              </motion.div>
            )}

          </AnimatePresence>

        </div>

      </div>

      {/* 0.8s Handshake Settlement Notification Toast */}
      {toast && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-black/90 backdrop-blur-md border border-cyan-400 rounded-2xl p-4 shadow-[0_0_30px_rgba(34,211,238,0.4)] z-[200] flex items-center gap-3 animate-in fade-in slide-in-from-bottom">
          <div className="w-5 h-5 rounded-full bg-cyan-400/20 border border-cyan-400 flex items-center justify-center shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></div>
          </div>
          <span className="text-cyan-100 font-mono text-[10px] sm:text-xs tracking-widest font-black uppercase whitespace-nowrap">
            {toast}
          </span>
        </div>
      )}

    </div>
  );
};
